import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Box, Container, Typography, Accordion, AccordionSummary, AccordionDetails, Grid } from '@mui/material';
import { ExpandMore, HelpOutline } from '@mui/icons-material';
import { servicesColors as colors } from '../theme';
const faqs = [
    {
        question: 'What does Free actually include?',
        answer: '25 AI-scored resumes (one-time, not monthly), up to 3 active jobs, and the ability to send our SMS qualification flow to your own verified phone—so you can experience what your candidates would. No credit card required.',
    },
    {
        question: 'Why can\'t I text candidates on the Free plan?',
        answer: 'Free is a true trial sandbox—you text yourself so you can see how the qualification flow feels from the candidate side. To message real candidates, upgrade to Starter or Advanced. This keeps Free fast and free for everyone instead of being abused as a generic SMS tool.',
    },
    {
        question: 'How does pricing compare to a staffing agency?',
        answer: 'A typical staffing agency adds about 30% markup on hourly wages for 90 days—roughly $1,400+ per hourly hire. Starter at $99/mo covers most SMB hiring volume; Advanced at $249/mo covers multi-site and peak-season hiring. Most teams break even on their first direct hire.',
    },
    {
        question: 'How do I upgrade to Starter or Advanced?',
        answer: 'Click \"Get Started\" on any paid plan to subscribe instantly via Stripe. You can upgrade, downgrade, or cancel anytime from your dashboard. 30-day money-back guarantee on all paid plans.',
    },
    {
        question: 'What certifications can the AI detect?',
        answer: 'Our AI recognizes all major trade certifications including EPA 608 (all sections), ASE certifications (A1-A9, L1-L3, etc.), NATE certification, OSHA 10/30, Master Plumber/Electrician licenses, CDL classifications, NFPA certifications, and state-specific contractor licenses. It also extracts license numbers for verification.',
    },
    {
        question: 'Can I use resumes that aren\'t in digital format?',
        answer: 'Absolutely! Many service businesses receive paper applications or walk-in resumes. Our system accepts PDF, DOC, DOCX, images (JPG, PNG), and even scanned documents. Our OCR technology extracts text from photos of handwritten applications too.',
    },
    {
        question: 'How does it handle experience with specific equipment or brands?',
        answer: 'The AI extracts and matches equipment-specific experience. For HVAC, it recognizes brands like Carrier, Trane, Lennox. For auto, it detects experience with diagnostic tools, specific vehicle makes, and specialty systems. This helps match candidates to your shop\'s specific needs.',
    },
    {
        question: 'Is there a limit on how many resumes I can upload?',
        answer: 'Our Free plan includes 25 AI-scored resumes (one-time grant, no monthly reset) and up to 3 active jobs. Growing service businesses can upgrade to Starter (1,500 scans/mo, $99) or Advanced (5,000 scans/mo, $249) instantly from the dashboard.',
    },
    {
        question: 'Can it verify that licenses are actually valid?',
        answer: 'StanorAI extracts license numbers and identifies the issuing authority. While we don\'t do live verification against state databases (licensing boards vary widely), we flag missing or potentially expired certifications based on dates and provide the info needed for quick manual verification.',
    },
    {
        question: 'How fast is the screening process?',
        answer: 'Most resumes are analyzed in under 10 seconds. A batch of 50 applications can be fully processed and ranked in under 5 minutes. Compare that to 3-4 hours of manual screening—you can respond to qualified candidates the same day they apply.',
    },
    {
        question: 'Do I need to set up the job requirements manually?',
        answer: 'You can let our AI analyze your job posting automatically, or use our industry templates. We have pre-built profiles for HVAC techs, auto mechanics, electricians, plumbers, and more—each with the most common required certifications and skills pre-loaded.',
    },
    {
        question: 'What if I hire across multiple trades?',
        answer: 'Perfect use case! Many facility management companies, property groups, and multi-service contractors use StanorAI. You can create separate job profiles for each trade, and our AI will score candidates against the right requirements automatically.',
    },
];
export const FaqSection = () => {
    const [expanded, setExpanded] = useState('panel0');
    const handleChange = (panel) => (_event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };
    return (_jsx(Box, { component: "section", sx: {
            py: { xs: 8, md: 12 },
            backgroundColor: '#ffffff',
        }, children: _jsx(Container, { maxWidth: "lg", children: _jsxs(Grid, { container: true, spacing: 6, children: [_jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsxs(Box, { sx: { position: 'sticky', top: 100 }, children: [_jsx(Box, { sx: {
                                        width: 56,
                                        height: 56,
                                        borderRadius: 3,
                                        background: colors.gradient,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 3,
                                    }, children: _jsx(HelpOutline, { sx: { color: '#ffffff', fontSize: 28 } }) }), _jsx(Typography, { variant: "h2", sx: {
                                        fontWeight: 700,
                                        color: colors.text,
                                        fontSize: { xs: '1.75rem', md: '2.25rem' },
                                        mb: 2
                                    }, children: "Questions About AI Screening for Trades?" }), _jsx(Typography, { sx: {
                                        color: colors.textSecondary,
                                        fontSize: '1rem',
                                        lineHeight: 1.7
                                    }, children: "We've helped hundreds of service businesses modernize their hiring. Here are the questions we hear most often." })] }) }), _jsx(Grid, { size: { xs: 12, md: 8 }, children: faqs.map((faq, index) => (_jsxs(Accordion, { expanded: expanded === `panel${index}`, onChange: handleChange(`panel${index}`), elevation: 0, disableGutters: true, sx: {
                                backgroundColor: 'transparent',
                                borderBottom: '1px solid',
                                borderColor: colors.divider,
                                '&:before': { display: 'none' },
                                '&.Mui-expanded': {
                                    margin: 0,
                                },
                            }, children: [_jsx(AccordionSummary, { expandIcon: _jsx(Box, { sx: {
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor: expanded === `panel${index}`
                                                ? colors.primary
                                                : colors.primaryLight,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.3s ease',
                                        }, children: _jsx(ExpandMore, { sx: {
                                                color: expanded === `panel${index}`
                                                    ? '#ffffff'
                                                    : colors.primary,
                                                transition: 'all 0.3s ease',
                                            } }) }), sx: {
                                        py: 2,
                                        px: 0,
                                        '& .MuiAccordionSummary-content': {
                                            margin: '12px 0',
                                        },
                                    }, children: _jsx(Typography, { sx: {
                                            fontWeight: 600,
                                            color: colors.text,
                                            fontSize: '1.05rem',
                                            pr: 2
                                        }, children: faq.question }) }), _jsx(AccordionDetails, { sx: { px: 0, pb: 3 }, children: _jsx(Typography, { sx: {
                                            color: colors.textSecondary,
                                            fontSize: '0.95rem',
                                            lineHeight: 1.8
                                        }, children: faq.answer }) })] }, index))) })] }) }) }));
};
